import { Permissions } from '../constants';
import Notifications from '../ui/molecules/toast/Notifications';
import { Flows } from './enum_constants/decision';
import { toast } from 'react-toastify';

//@ts-ignore
import JSZip from 'jszip';

/* eslint-disable unicorn/prefer-add-event-listener */
export function debounce<T extends (...args: any[]) => void>(
  func: T,
  delay: number
) {
  let timer: ReturnType<typeof setTimeout>;
  return function (...args: Parameters<T>) {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func(...args);
    }, delay);
  };
}
export const saveFileToIndexedDB = (file: File, id: string): void => {
  const request = indexedDB.open('fileStore', 1);

  request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
    const db = (event.target as IDBOpenDBRequest).result;
    if (!db.objectStoreNames.contains('files')) {
      db.createObjectStore('files', { keyPath: 'id' });
    }
  };

  request.onsuccess = (event: Event) => {
    const db = (event.target as IDBOpenDBRequest).result;
    const transaction = db.transaction('files', 'readwrite');
    const store = transaction.objectStore('files');

    // Save the file with its unique id
    store.put({ id, file });

    console.log(`File saved to IndexedDB with id: ${id}`);
  };

  request.onerror = (event: Event) => {
    console.error(
      'Error saving file to IndexedDB:',
      (event.target as IDBOpenDBRequest).error
    );
  };
};

export const loadFileFromIndexedDB = (id: string): Promise<File> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('fileStore', 1);

    request.onsuccess = (event: Event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      const transaction = db.transaction('files', 'readonly');
      const store = transaction.objectStore('files');

      const getRequest = store.get(id);

      getRequest.onsuccess = (event: Event) => {
        const result = (event.target as IDBRequest).result;
        if (result?.file) {
          const file = result.file as File;
          resolve(file); // Return the object URL
        } else {
          reject(new Error('File not found.'));
        }
      };

      getRequest.onerror = (event: Event) => {
        reject(new Error('Error retrieving file from IndexedDB.'));
      };
    };

    request.onerror = (event: Event) => {
      reject(new Error('Error opening IndexedDB.'));
    };
  });
};

export const removeFileFromIndexedDB = (id: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('fileStore', 1);

    request.onsuccess = (event: Event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      const transaction = db.transaction('files', 'readwrite');
      const store = transaction.objectStore('files');

      const deleteRequest = store.delete(id);

      deleteRequest.onsuccess = () => {
        console.log(`File with id '${id}' removed from IndexedDB.`);
        resolve();
      };

      deleteRequest.onerror = (event: Event) => {
        reject(new Error('Error removing file from IndexedDB.'));
      };
    };

    request.onerror = (event: Event) => {
      reject(new Error('Error opening IndexedDB.'));
    };
  });
};

const isValidUrl = (url: string): boolean => {
  const trustedDomains = [process.env.REACT_APP_API_DOMAIN].filter(
    Boolean
  ) as string[];
  try {
    const parsedUrl = new URL(url);
    return trustedDomains.some((domain) => parsedUrl.hostname.endsWith(domain));
  } catch {
    return false;
  }
};

export const downloadFileFromUrl = async (
  url: string,
  fileName: string,
  init?: RequestInit
) => {
  if (!isValidUrl(url)) {
    Notifications('The provided URL is not trusted or valid.', 0);
    return;
  }

  try {
    const response = await fetch(url, { credentials: 'include', ...init });

    if (!response.ok) {
      throw new Error(`Failed to fetch file: ${response.statusText}`);
    }

    //Downloads do not content type
    // const allowedContentTypes = ['image/png', 'image/jpeg', 'application/pdf'];
    // const contentType = response.headers.get('Content-Type');
    // if (!contentType || !allowedContentTypes.includes(contentType)) {
    //  throw new Error('Unsupported or unsafe file type detected.');
    // }

    const blob = await response.blob();
    const objectUrl = globalThis.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = objectUrl; // Safe as inputs are validated
    link.download = fileName || 'downloaded-file';
    document.body.append(link);
    link.click();

    // Clean up
    link.remove();
    globalThis.URL.revokeObjectURL(objectUrl);
  } catch (error: any) {
    Notifications(`Error downloading file: ${error?.message || ''}`, 0);
  }
};

export const downloadMultipleFiles = async (
  files: { url: string; fileName: string }[],
  init?: RequestInit
): Promise<void> => {
  if (!files || files.length === 0) {
    console.error('No files to download.');
    return;
  }

  try {
    await Promise.all(
      files.map((file) => downloadFileFromUrl(file.url, file.fileName, init))
    );
    Notifications('All files downloaded successfully.', 1);
  } catch (error) {
    console.error('Some files failed to download:', error);
    Notifications(
      'Some files failed to download. Check the console for details.',
      0
    );
  }
};
const getFileExtension = (fileName: string): string => {
  if (!fileName) return '';
  const parts = fileName.split('.');
  return parts.length > 1 ? parts.pop()!.split('?')[0].toLowerCase() : '';
};

export const sameFileExtension = (
  filename1: string,
  filename2: string
): string => {
  if (filename2 && !/\.[^./]+$/.test(filename2)) {
    const ext = getFileExtension(filename1);
    if (ext) {
      filename2 = `${filename2}.${ext}`;
    }
  }
  return filename2;
};
export const downloadMultipleFilesAsZip = async (
  files: { url: string; fileName?: string }[],
  zipFileName: string,
  init?: RequestInit
): Promise<void> => {
  if (!files || files.length === 0) {
    console.error('No files to download.');
    return;
  }

  const zip = new JSZip();
  const totalFiles = files.length;
  let completedFiles = 0;

  // Show a loading toast
  const toastId = toast.loading(`Downloading 0/${totalFiles} files...`);

  try {
    const filePromises = files.map(async (file, index) => {
      const response = await fetch(file.url, {
        credentials: 'include',
        ...init,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${file.url}`);
      }

      const extension = file.url.split('.').pop()?.toLowerCase() || 'unknown';
      const fileName = file.fileName || `file-${index + 1}.${extension}`;

      const blob = await response.blob();
      zip.file(fileName, blob);

      // Update progress
      completedFiles += 1;
      const progress = completedFiles / totalFiles;

      toast.update(toastId, {
        render: `Downloading ${completedFiles}/${totalFiles} files...`,
        type: 'info',
        isLoading: true, // Keeps the loading state
        progress, // Optional progress bar
      });
    });

    await Promise.all(filePromises);

    // Generate the ZIP file
    const zipBlob = await zip.generateAsync({ type: 'blob' });

    // Create a download link for the ZIP file
    const link = document.createElement('a');
    const objectUrl = globalThis.URL.createObjectURL(zipBlob);

    link.href = objectUrl;
    link.download = zipFileName;
    document.body.append(link);
    link.click();

    link.remove();
    globalThis.URL.revokeObjectURL(objectUrl);

    toast.update(toastId, {
      render: 'Files downloaded successfully!',
      type: 'success',
      isLoading: false, // End the loading state
      autoClose: 3000, // Auto-close after 5 seconds
    });

    setTimeout(() => {
      toast.dismiss(toastId);
    }, 3000);
  } catch (error: any) {
    console.error('Failed to download files as ZIP:', error);

    // Update the toast to error
    toast.update(toastId, {
      render:
        'Failed to download files as a ZIP. Check the console for details.',
      type: 'error',
      isLoading: false, // End the loading state
      autoClose: 3000,
    });

    setTimeout(() => {
      toast.dismiss(toastId);
    }, 3000);
  }
};

const isPersistEnabled = (): boolean => {
  const url = new URL(globalThis.location.href);
  const queryParams = new URLSearchParams(globalThis?.location.search);
  const isNotFValue = '' + queryParams.get('f') !== '1';
  const isRValue = '' + queryParams.get('r') === '1';

  if (isRValue) {
    return false;
  }
  if (url.pathname.slice(1) === Flows?.Audit && isNotFValue) {
    return true;
  }

  // if(isComingFromMainWindow()){
  //   return true
  // }

  return false;
};

export const checkPermission = (
  permissions: string[],
  requiredPermission: Permissions
) => {
  if (!permissions.includes(requiredPermission)) {
    throw new Error(
      'PermissionDenied: You do not have permission to perform this action.'
    );
  }
};

export const Validation = {
  isPersistEnabled,
};
